import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
	Checkbox,
	DatePicker,
	Flex,
	Form,
	Input,
	Segmented,
	Select,
	Space,
	Typography,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect, useRef, useState } from "react";
import { useAgents } from "@/common/artemis-operations/agents";
import { useTaskBlastRadius } from "@/common/artemis-operations/blast-radius";
import { useEnvironments } from "@/common/artemis-operations/environments";
import { useProject, useProjects } from "@/common/artemis-operations/projects";
import {
	useCreateTaskTemplate,
	useUpdateTaskTemplate,
} from "@/common/artemis-operations/task-templates";
import {
	useCreateTask,
	useDispatchTaskById,
} from "@/common/artemis-operations/tasks";
import type { AssigneeType } from "@/common/artemis-sdk/graphql";
import {
	ERROR_TASK_CREATION_DESC,
	ERROR_TASK_CREATION_TITLE,
	ERROR_TASK_DISPATCH_DESC,
	ERROR_TASK_DISPATCH_TITLE,
	ERROR_TEMPLATE_SAVE_DESC,
	ERROR_TEMPLATE_SAVE_TITLE,
	TASK_CREATED_DESC,
	TASK_CREATED_TITLE,
	TASK_DISPATCHED_INFO_COMPLETED,
	TEMPLATE_CREATED_TITLE,
	TEMPLATE_SAVED_DESC,
	TEMPLATE_UPDATED_TITLE,
} from "@/common/messages";
import { agentIsDispatchable, EnvironmentClassification } from "@/common/model";
import {
	findPlaceholderKeys,
	type PlaceholderField,
	syncPlaceholders,
	toTaskTemplatePlaceholderInput,
} from "@/common/placeholders";
import { useNotify } from "@/common/use-notify";
import { AgentSelector } from "@/components/agents/agent-selector";
import { PlaceholderTextArea } from "@/components/placeholders/placeholder-text-area";
import {
	PLACEHOLDERS_FIELD,
	TemplatePlaceholdersEditor,
} from "@/components/placeholders/template-placeholders-editor";
import { UserSearchSelect } from "@/components/users/user-search-select";
import { Wizard } from "@/components/wizard";
import { EnvironmentClassificationBadge } from "../environments/environment-badges";
import { SegmentedSelection } from "../segmented-selection";
import { TaskBlastRadiusHint } from "./task-blast-radius-hint";

/** Values used to pre-fill the wizard (e.g. when duplicating an existing task). */
export interface TaskWizardInitialValues {
	name: string;
	description: string;
	projectId?: string | null;
	instructions?: string | null;
	deploymentIds?: string[] | null;
	environmentIds?: string[] | null;
	assigneeId?: string | null;
	assigneeType?: AssigneeType | null;
	verificationCriteria?: string | null;
	/** Template kind only: the template's declared `{{key}}` placeholders. */
	placeholders?: PlaceholderField[] | null;
}

/** The text fields a template's `{{key}}` placeholders may appear in. */
const PLACEHOLDER_TEXT_FIELDS = [
	"name",
	"description",
	"instructions",
	"verificationCriteria",
] as const;

/** Under a template's text fields: how to add a placeholder. */
const PLACEHOLDER_HINT =
	"Type {{key}} to add a placeholder, asked for when a task is created from this template.";

interface TaskCreateWizardProps {
	open: boolean;
	onClose: () => void;
	/** When provided, the wizard opens pre-filled with these values. */
	initialValues?: TaskWizardInitialValues;
	/**
	 * What the wizard saves. "task" (the default) creates a real task and optionally
	 * dispatches it; "template" saves the same field set as a reusable task template,
	 * which is inert — nothing is dispatched and no navigation happens.
	 */
	kind?: "task" | "template";
	/** In template kind, the template being edited. Omitted means create a new one. */
	templateId?: string;
}

const timeReferencePresets = [
	{ value: "last_30_minutes", label: "Last 30 minutes" },
	{ value: "last_hour", label: "Last hour" },
	{ value: "last_24_hours", label: "Last 24 hours" },
	{ value: "last_7_days", label: "Last 7 days" },
	{ value: "last_30_days", label: "Last 30 days" },
	{ value: "custom", label: "Custom date/time…" },
];

const commonInstructionsSet: { value: string; label: string }[] = [
	{
		value: "Create a workspace report",
		label: "Create a report in the workspace",
	},
	{
		value: "Documents must be PDF",
		label: "Create PDF Documents",
	},
	{
		value: "Attach supporting CloudWatch charts as evidence",
		label: "Attach charts as evidence",
	},
	{
		value: "Upload created artifacts as assets",
		label: "Upload created artifacts",
	},
	{
		value: "Force read project documents",
		label: "Force read project documents",
	},
	{
		value: "Include remediation recommendations, not just findings",
		label: "Include remediations",
	},
	{
		value: "Plan only — describe the change, do not execute it",
		label: "Plan only (dry run)",
	},
];

/** Wizard for creating a new task. */
export function TaskCreateWizard({
	open,
	onClose,
	initialValues,
	kind = "task",
	templateId,
}: TaskCreateWizardProps) {
	const isTemplate = kind === "template";
	const { orgSlug, workspaceId } = useParams({ strict: false }) as {
		orgSlug: string;
		workspaceId: string;
	};
	const navigate = useNavigate();
	const { data: environments } = useEnvironments(workspaceId);
	const { data: projects } = useProjects(workspaceId);
	const { data: agents } = useAgents(workspaceId);
	const firstEnabledAgentId = (agents ?? []).find(
		(a) => a?.status === "ENABLED",
	)?.id;

	const [loading, setLoading] = useState(false);
	const [mode, setMode] = useState<"express" | "advanced">("express");
	// Whether the task targets a project (with optional deployments/environments) or
	// a set of environments directly, with no project at all.
	const [targetMode, setTargetMode] = useState<"project" | "environment">(
		"project",
	);
	// Defaults to an agent (pre-selected to the first available one below); the
	// "assign later" checkbox opts out of assigning anyone at creation time.
	const [assigneeMode, setAssigneeMode] = useState<"agent" | "user">("agent");
	const [assignLater, setAssignLater] = useState(false);
	const [form] = Form.useForm();
	const createTask = useCreateTask();
	const dispatchTaskById = useDispatchTaskById();
	const createTaskTemplate = useCreateTaskTemplate();
	const updateTaskTemplate = useUpdateTaskTemplate();

	const queryClient = useQueryClient();
	const notify = useNotify();

	const values = Form.useWatch([], form);

	// Template kind keeps its placeholder list in step with the `{{key}}` tokens in its
	// text. `preserve` so a token in the advanced-only fields still counts in express mode.
	const watchedName = Form.useWatch("name", { form, preserve: true });
	const watchedDescription = Form.useWatch("description", {
		form,
		preserve: true,
	});
	const watchedInstructions = Form.useWatch("instructions", {
		form,
		preserve: true,
	});
	const watchedVerification = Form.useWatch("verificationCriteria", {
		form,
		preserve: true,
	});
	// Settings of placeholders whose token was deleted, restored if it is typed again.
	const rememberedPlaceholders = useRef(new Map<string, PlaceholderField>());
	// biome-ignore lint/correctness/useExhaustiveDependencies: the watched text is the trigger; the effect reads the form store directly so it also sees values seeded in the same commit
	useEffect(() => {
		if (!isTemplate) return;
		const all = form.getFieldsValue(true);
		const current: PlaceholderField[] = all[PLACEHOLDERS_FIELD] ?? [];
		const keys = findPlaceholderKeys(
			...PLACEHOLDER_TEXT_FIELDS.map((f) => all[f] as string | undefined),
		);
		if (keys.join("\0") === current.map((p) => p.key).join("\0")) return;
		for (const p of current) rememberedPlaceholders.current.set(p.key, p);
		form.setFieldValue(
			PLACEHOLDERS_FIELD,
			syncPlaceholders(current, keys, rememberedPlaceholders.current),
		);
	}, [
		isTemplate,
		form,
		watchedName,
		watchedDescription,
		watchedInstructions,
		watchedVerification,
	]);

	// What the task would touch, judged from the name and description as they are typed.
	// Advisory decoration: it gates nothing, and createTask never calls it.
	const {
		hint: blastRadius,
		loading: blastRadiusLoading,
		onDescriptionBlur,
		evaluate: evaluateBlastRadius,
		stale: blastRadiusStale,
		visible: blastRadiusVisible,
		hasEnoughText: blastRadiusHasText,
	} = useTaskBlastRadius(
		(values?.name as string) ?? "",
		(values?.description as string) ?? "",
		((values?.environmentIds as string[]) ?? [])[0] ?? null,
	);

	const { data: project } = useProject(values?.projectId);
	const deploymentOptions: { value: string; label: string }[] = (
		project?.deployments ?? []
	).map((d) => ({
		value: d.id,
		label: `${d.name}`,
	}));

	// Project flow: a deployment must be picked; only a project with NO deployments
	// falls back to direct environment selection. Gate on the loaded project record so
	// the environment select doesn't flash while deployments are still being fetched.
	const projectLoaded = Boolean(values?.projectId) && project != null;
	const projectHasDeployments = deploymentOptions.length > 0;
	// Deployment/environment selectors are always visible; these gate which one is
	// interactive (and required) for the current target + project state. In project mode
	// environments are only ever resolved from deployments — the picker stays disabled even
	// when the selected project has none.
	const deploymentEnabled =
		targetMode === "project" && projectLoaded && projectHasDeployments;
	// A task must name the deployments it acts on, but a template is a starting point:
	// leaving deployments open (the backend reads an empty list as "all deployments")
	// is a legitimate choice, and the user picks them when creating the actual task.
	const deploymentRequired = deploymentEnabled && !isTemplate;
	const showEnvironmentSelect = targetMode === "environment";

	const reset = () => {
		setLoading(false);
		setMode("express");
		setTargetMode("project");
		setAssigneeMode("agent");
		setAssignLater(false);
		rememberedPlaceholders.current.clear();
		form.resetFields();
	};

	// Seed the form whenever the wizard opens with initial values (duplicate flow).
	useEffect(() => {
		if (open && initialValues) {
			// Advanced-only fields need advanced mode to be visible/editable.
			if (initialValues.instructions || initialValues.verificationCriteria)
				setMode("advanced");
			setTargetMode(initialValues.projectId ? "project" : "environment");
			const hasAssignee = Boolean(
				initialValues.assigneeId && initialValues.assigneeType,
			);
			setAssignLater(!hasAssignee);
			setAssigneeMode(
				hasAssignee && initialValues.assigneeType === "USER" ? "user" : "agent",
			);
			form.setFieldsValue({
				name: initialValues.name,
				description: initialValues.description,
				projectId: initialValues.projectId,
				deploymentIds: initialValues.deploymentIds ?? [],
				environmentIds: initialValues.environmentIds ?? [],
				assigneeId: initialValues.assigneeId ?? undefined,
				instructions: initialValues.instructions ?? undefined,
				verificationCriteria: initialValues.verificationCriteria ?? undefined,
				[PLACEHOLDERS_FIELD]: initialValues.placeholders ?? [],
			});
		}
	}, [open, initialValues, form]);

	// Blank wizard defaults its assignee to the first available agent. Agents may load
	// after the drawer opens, so key on the resolved id and don't clobber a manual pick.
	useEffect(() => {
		if (
			open &&
			!initialValues &&
			firstEnabledAgentId &&
			!form.getFieldValue("assigneeId")
		) {
			form.setFieldValue("assigneeId", firstEnabledAgentId);
		}
	}, [open, initialValues, firstEnabledAgentId, form]);

	const handleComplete = (startImmediately: boolean) => {
		form.validateFields().then((values) => {
			setLoading(true);

			const timeReference =
				values.timeReferencePreset === "custom"
					? values.timeReferenceCustom &&
						`Time reference: ${values.timeReferenceCustom.format("YYYY-MM-DD HH:mm")}`
					: values.timeReferencePreset &&
						`Time reference: ${
							timeReferencePresets.find(
								(p) => p.value === values.timeReferencePreset,
							)?.label
						}`;

			const assigneeType: AssigneeType | undefined = assignLater
				? undefined
				: assigneeMode === "agent"
					? "AGENT"
					: "USER";

			// express mode leaves the advanced fields empty; only emit what's set.
			const instructionParts = [
				...((values.commonInstructions as string[]) ?? []),
				timeReference,
				values.instructions,
			].filter(Boolean);

			// Shared by both kinds — a template holds exactly the task's definition fields.
			const definition = {
				name: values.name,
				projectId:
					targetMode === "project" ? (values.projectId as string) : undefined,
				assigneeId: assignLater ? undefined : values.assigneeId,
				assigneeType,
				description: values.description,
				verificationCriteria:
					(values.verificationCriteria as string) || undefined,
				deploymentIds:
					targetMode === "project"
						? ((values.deploymentIds as string[]) ?? [])
						: [],
				environmentIds: showEnvironmentSelect
					? ((values.environmentIds as string[]) ?? [])
					: [],
				// Free text alone passes through unchanged (a duplicated task's instructions
				// round-trip verbatim); the bullet list only composes multiple parts.
				instructions:
					instructionParts.length === 1 &&
					instructionParts[0] === values.instructions
						? values.instructions
						: instructionParts.length
							? `\n- ${instructionParts.join("\n- ")}`
							: "",
			};

			if (isTemplate) {
				// Only placeholders still referenced by the text being saved (e.g. not one
				// that lived in instructions dropped by switching to express mode).
				const usedKeys = new Set(
					findPlaceholderKeys(
						definition.name,
						definition.description,
						definition.instructions,
						definition.verificationCriteria,
					),
				);
				const placeholders = (
					(form.getFieldValue(PLACEHOLDERS_FIELD) as PlaceholderField[]) ?? []
				)
					.filter((p) => usedKeys.has(p.key))
					.map(toTaskTemplatePlaceholderInput);
				const onSuccess = () => {
					notify.success(
						templateId ? TEMPLATE_UPDATED_TITLE : TEMPLATE_CREATED_TITLE,
						TEMPLATE_SAVED_DESC,
					);
					reset();
					onClose();
				};
				const onError = () => {
					setLoading(false);
					notify.error(ERROR_TEMPLATE_SAVE_TITLE, ERROR_TEMPLATE_SAVE_DESC);
				};

				if (templateId) {
					// projectId "" clears the project server-side; undefined would mean "unchanged",
					// which would strand a template that just switched to environment targeting.
					updateTaskTemplate(
						{
							taskTemplateId: templateId,
							input: {
								...definition,
								projectId: definition.projectId ?? "",
								placeholders,
							},
						},
						{ onSuccess, onError },
					);
				} else {
					createTaskTemplate(
						{ ...definition, workspaceId, placeholders },
						{ onSuccess, onError },
					);
				}
				return;
			}

			createTask(
				{ ...definition, workspaceId },
				{
					onSuccess: (task) => {
						notify.success(TASK_CREATED_TITLE, TASK_CREATED_DESC);
						queryClient.invalidateQueries({ queryKey: ["tasks"] }); // todo: this might be expensive
						if (startImmediately && assigneeType === "AGENT") {
							dispatchTaskById(task.id, {
								onSuccess: () => notify.success(TASK_DISPATCHED_INFO_COMPLETED),
								onError: () =>
									notify.error(
										ERROR_TASK_DISPATCH_TITLE,
										ERROR_TASK_DISPATCH_DESC,
									),
							});
						}
						reset();
						onClose();
						// Duplicate flow (wizard opened pre-filled): jump to the new task.
						if (initialValues) {
							navigate({
								to: "/$orgSlug/workspaces/$workspaceId/tasks/$taskId",
								params: { orgSlug, workspaceId, taskId: task.id },
							});
						}
					},
					onError: () => {
						setLoading(false);
						notify.error(ERROR_TASK_CREATION_TITLE, ERROR_TASK_CREATION_DESC);
					},
				},
			);
		});
	};

	// With an agent assigned, "Create + Start" is the primary footer action and
	// plain "Create" moves into the dropdown; otherwise only "Create" is offered. An agent with
	// no A2A endpoint can't be dispatched to (mirrors the backend guard), so it only gets "Create".
	const selectedAgent = agents?.find((a) => a?.id === values?.assigneeId);
	const canStartImmediately =
		assigneeMode === "agent" &&
		!assignLater &&
		agentIsDispatchable(selectedAgent);

	const pages = [
		{
			id: "scope",
			title: "Scope",
			content: (
				<Space orientation="vertical" size={"middle"} style={{ width: "100%" }}>
					<Form.Item label="Target">
						<SegmentedSelection
							value={targetMode}
							onChange={(v) => {
								setTargetMode(v as "project" | "environment");
								form.setFieldsValue({
									projectId: undefined,
									deploymentIds: [],
									environmentIds: [],
								});
							}}
							options={[
								{
									value: "project",
									label: "Project",
									description: "Work within a project and its deployments.",
								},
								{
									value: "environment",
									label: "Environment",
									description: "Work directly on one or more environments.",
								},
							]}
						/>
					</Form.Item>
					<Form.Item
						name="projectId"
						label="Project"
						rules={[{ required: targetMode === "project" }]}
					>
						<Select
							disabled={targetMode !== "project"}
							placeholder="Select a project"
							options={(projects ?? [])
								.filter((p) => p != null)
								.map((p) => ({ value: p.id, label: p.name }))}
							onChange={() =>
								form.setFieldsValue({
									deploymentIds: [],
									environmentIds: [],
								})
							}
						/>
					</Form.Item>
					<Form.Item
						name="deploymentIds"
						label="Deployments"
						style={{ marginBottom: 0 }}
						rules={
							deploymentRequired
								? [
										{
											required: true,
											message: "Select at least one deployment",
										},
									]
								: []
						}
						extra="Select one or more deployments this task should work on. The assigned agent resolves each deployment's environment automatically."
					>
						<Select
							mode="multiple"
							disabled={!deploymentEnabled}
							placeholder="Select deployments"
							options={deploymentOptions}
							optionRender={(option) => (
								<Flex align="center" justify="space-between" gap={8}>
									<Typography.Text>{option.data.label}</Typography.Text>
									<Flex gap={4}>
										<EnvironmentClassificationBadge
											classification={
												environments?.find((e) => e?.id === option.data.value)
													?.classification ??
												EnvironmentClassification.NON_PRODUCTION
											}
										/>
									</Flex>
								</Flex>
							)}
						/>
					</Form.Item>
					<Form.Item
						name="environmentIds"
						label="Environments"
						style={{ marginBottom: 0 }}
						rules={
							targetMode === "environment"
								? [
										{
											required: true,
											message: "Select at least one environment",
										},
									]
								: []
						}
						extra="The environments this task operates on."
					>
						<Select
							mode="multiple"
							disabled={!showEnvironmentSelect}
							placeholder="Select environments"
							options={(environments ?? [])
								.filter((e) => e != null)
								.map((e) => ({ value: e.id, label: e.name }))}
							optionRender={(option) => (
								<Flex align="center" justify="space-between" gap={8}>
									<Typography.Text>{option.data.label}</Typography.Text>
									<EnvironmentClassificationBadge
										classification={
											environments?.find((e) => e?.id === option.data.value)
												?.classification ??
											EnvironmentClassification.NON_PRODUCTION
										}
									/>
								</Flex>
							)}
						/>
					</Form.Item>
				</Space>
			),
		},
		{
			id: "details",
			title: "Details",
			content: (
				<Space orientation="vertical" size={"small"} style={{ width: "100%" }}>
					<Form.Item
						name="name"
						label="Name"
						rules={[{ required: true, min: 3, max: 500 }]}
					>
						<Input maxLength={500} placeholder="Task name" />
					</Form.Item>
					<Form.Item
						name="description"
						label="Description"
						rules={[{ required: true, min: 10, max: 5000 }]}
						extra={isTemplate ? PLACEHOLDER_HINT : undefined}
					>
						{isTemplate ? (
							<PlaceholderTextArea
								rows={6}
								maxLength={5000}
								showCount
								placeholder="Describe the task"
							/>
						) : (
							<Input.TextArea
								rows={6}
								maxLength={5000}
								showCount
								placeholder="Describe the task"
								onBlur={onDescriptionBlur}
							/>
						)}
					</Form.Item>
					{/* ponytail: disabled for now, ship later. Advisory only, and never on the create path: a missing hint renders nothing. */}
					{false && blastRadiusVisible && (
						<TaskBlastRadiusHint
							hint={blastRadius}
							loading={blastRadiusLoading}
							onEvaluate={evaluateBlastRadius}
							stale={blastRadiusStale}
							hasEnoughText={blastRadiusHasText}
						/>
					)}
				</Space>
			),
		},
		{
			id: "assignee",
			title: "Assignee",
			content: (
				<Space orientation="vertical" size={"small"} style={{ width: "100%" }}>
					{/* The step title is the heading, so the field needs no second label. */}
					<Form.Item>
						<Space vertical style={{ width: "100%" }}>
							{/* "Assign this later" disables — but keeps visible — the assignee inputs. */}
							<div
								style={{
									width: "100%",
									pointerEvents: assignLater ? "none" : undefined,
									opacity: assignLater ? 0.45 : 1,
								}}
							>
								<Space vertical style={{ width: "100%" }}>
									<SegmentedSelection
										value={assigneeMode}
										onChange={(v) => {
											const next = v as "agent" | "user";
											setAssigneeMode(next);
											// Re-seed the first agent when switching back to agent; clear otherwise.
											form.setFieldValue(
												"assigneeId",
												next === "agent" ? firstEnabledAgentId : undefined,
											);
										}}
										options={[
											{
												value: "agent",
												label: "Agent",
												description: "Assign to an AI agent.",
											},
											{
												value: "user",
												label: "User",
												description: "Assign to a teammate.",
											},
										]}
									/>
									{assigneeMode === "agent" && (
										<Form.Item
											name="assigneeId"
											rules={[
												{ required: !assignLater, message: "Select an agent" },
											]}
											noStyle
										>
											<AgentSelector
												workspaceId={workspaceId}
												onAgentChange={(aid) =>
													form.setFieldValue("assigneeId", aid)
												}
											/>
										</Form.Item>
									)}
									{assigneeMode === "user" && (
										<Form.Item
											name="assigneeId"
											label="User"
											rules={[
												{ required: !assignLater, message: "Select a user" },
											]}
										>
											<UserSearchSelect />
										</Form.Item>
									)}
								</Space>
							</div>
							<Checkbox
								checked={assignLater}
								onChange={(e) => {
									setAssignLater(e.target.checked);
									form.setFieldValue(
										"assigneeId",
										e.target.checked ? undefined : firstEnabledAgentId,
									);
								}}
							>
								Assign this later
							</Checkbox>
						</Space>
					</Form.Item>
				</Space>
			),
		},
		...(mode === "advanced" ? advancedPages(values, isTemplate) : []),
		...(isTemplate
			? [
					{
						id: "placeholders",
						title: "Placeholders",
						content: <TemplatePlaceholdersEditor />,
					},
				]
			: []),
	];

	return (
		<Wizard
			open={open}
			title={
				isTemplate
					? templateId
						? "Edit Template"
						: "Save as Template"
					: "Create Task"
			}
			extra={
				<Segmented
					value={mode}
					onChange={(v) => setMode(v as "express" | "advanced")}
					options={[
						{ value: "express", label: "Express" },
						{ value: "advanced", label: "Advanced" },
					]}
				/>
			}
			completeLabel={
				isTemplate
					? "Save template"
					: canStartImmediately
						? "Create + Start"
						: "Create"
			}
			onComplete={() => handleComplete(!isTemplate && canStartImmediately)}
			completeMenu={
				!isTemplate && canStartImmediately
					? [
							{
								key: "create-only",
								label: "Create",
								onClick: () => handleComplete(false),
							},
						]
					: undefined
			}
			form={form}
			loading={loading}
			size="large"
			onCancel={() => {
				onClose();
				reset();
			}}
			pages={pages}
		/>
	);
}

/** Instructions + verification pages, shown only in advanced mode. A template's text
 * fields highlight their `{{key}}` placeholders. */
function advancedPages(
	values: { timeReferencePreset?: string },
	isTemplate: boolean,
) {
	const LongText = isTemplate ? PlaceholderTextArea : TextArea;
	return [
		{
			id: "extra",
			title: "Instructions",
			content: (
				<Space orientation="vertical" size={"middle"} style={{ width: "100%" }}>
					<Form.Item
						name="commonInstructions"
						label="Quick instructions"
						style={{ marginBottom: 0 }}
					>
						<Select
							mode="multiple"
							placeholder="Select common instructions"
							options={commonInstructionsSet}
						/>
					</Form.Item>
					<Form.Item
						name="timeReferencePreset"
						label="Time reference"
						style={{ marginBottom: 0 }}
						extra="Optional - scopes the agent's work to a specific time window."
					>
						<Select
							allowClear
							placeholder="No time reference"
							options={timeReferencePresets}
						/>
					</Form.Item>
					{values?.timeReferencePreset === "custom" && (
						<Form.Item
							name="timeReferenceCustom"
							label="Custom date/time"
							rules={[{ required: true }]}
						>
							<DatePicker showTime style={{ width: "100%" }} />
						</Form.Item>
					)}
					<Form.Item
						name="instructions"
						label="Agent instructions"
						rules={[{ required: false }]}
						extra={`Instructions passed to the agent on each run. Describe what the agent should do, what data to analyze, and what artifacts to produce.${isTemplate ? ` ${PLACEHOLDER_HINT}` : ""}`}
					>
						<LongText
							rows={12}
							placeholder="e.g. Analyze the past 24h of error logs in the production environment. Summarize any recurring errors and create a markdown report. Upload the report as an artifact."
						/>
					</Form.Item>
					<Form.Item
						name="verificationCriteria"
						label="Verification Criteria"
						extra="Optional — describe what to check to confirm this task succeeded. Agents can also fill this in when they accept the task."
					>
						<LongText
							rows={6}
							placeholder="e.g. Check that the migration ran successfully, all tests pass, and the API returns 200 on the health endpoint."
						/>
					</Form.Item>
				</Space>
			),
		},
	];
}
